import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Flame, Mail, Lock, ArrowRight, ShieldCheck } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { HERO_IMAGES } from '../mock';

export default function Login() {
  const { login } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(form.email, form.password);
      toast({ title: `Welcome back, ${u.name.split(' ')[0]}!` });
      const from = location.state?.from;
      navigate(u.role === 'admin' ? (from || '/admin') : (from || '/'), { replace: true });
    } catch (err) {
      toast({ title: 'Login failed', description: err.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#070a12]">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO_IMAGES[0]} alt="Fire safety" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="orb w-[400px] h-[400px] bg-primary/25 top-10 -left-20 animate-glow" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="grid place-items-center w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent"><Flame className="w-5 h-5" /></span>
            <span className="font-display text-2xl font-extrabold tracking-wide">CLOSE<span className="fire-text">FIRE</span></span>
          </Link>
          <div>
            <div className="section-eyebrow fire-text"><ShieldCheck className="w-4 h-4 text-primary" /> Secure Access</div>
            <h2 className="font-heading font-extrabold text-4xl mt-4 max-w-md">Manage your fire-safety operations with confidence.</h2>
            <p className="text-gray-300 mt-4 max-w-md">Sign in to access the Closefire admin console and content management system.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-8">
            <span className="grid place-items-center w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent text-white"><Flame className="w-5 h-5" /></span>
            <span className="font-display text-2xl font-extrabold tracking-wide text-white">CLOSE<span className="fire-text">FIRE</span></span>
          </Link>
          <h1 className="font-heading font-extrabold text-4xl text-white">Welcome Back</h1>
          <p className="text-gray-400 mt-2">Sign in to your account to continue.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="text-gray-300">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input id="email" name="email" data-testid="login-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" className="h-12 pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-gray-300">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input id="password" name="password" data-testid="login-password-input" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" className="h-12 pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
              </div>
            </div>
            <Button type="submit" data-testid="login-submit-button" disabled={loading} size="lg" className="w-full gap-2 h-12 bg-gradient-to-r from-primary to-accent hover:opacity-90">
              {loading ? 'Signing in…' : <>Sign In <ArrowRight className="w-4 h-4" /></>}
            </Button>
          </form>

          <div className="mt-6 p-4 rounded-xl glass text-sm">
            <p className="font-semibold text-white">Demo admin credentials</p>
            <p className="text-gray-400 mt-1">admin@closefire.in &nbsp;/&nbsp; admin123</p>
          </div>

          <p className="text-center text-gray-400 mt-6">Don’t have an account? <Link to="/register" className="text-primary font-semibold hover:underline">Create one</Link></p>
        </motion.div>
      </div>
    </div>
  );
}
